/**
 * Preserve the independent details scroller. Once the media reaches the bottom
 * of the viewport, spend further downward page movement on unread details first.
 */
class ProductDetailsComponent extends HTMLElement {
  #desktop = window.matchMedia('(min-width: 750px)');
  /** @type {HTMLElement | null} */
  #scroller = null;
  /** @type {HTMLElement | null} */
  #grid = null;
  #previousScrollY = 0;

  connectedCallback() {
    this.#scroller = this.querySelector(':scope > .group-block');
    this.#grid = this.closest('.product-information__grid');
    this.#desktop.addEventListener('change', this.#updateListener);
    this.#updateListener();
  }

  disconnectedCallback() {
    this.#desktop.removeEventListener('change', this.#updateListener);
    window.removeEventListener('scroll', this.#onPageScroll);
  }

  #updateListener = () => {
    window.removeEventListener('scroll', this.#onPageScroll);
    this.#previousScrollY = window.scrollY;

    if (this.#desktop.matches && this.classList.contains('sticky-content--desktop')) {
      window.addEventListener('scroll', this.#onPageScroll, { passive: true });
    }
  };

  #onPageScroll = () => {
    // Keep the pre-modal baseline so restoring the page position on close does
    // not count as a new downward scroll through the product section.
    if (document.documentElement.hasAttribute('scroll-lock') || document.body.style.position === 'fixed') return;

    const currentY = window.scrollY;
    const previousY = this.#previousScrollY;
    this.#previousScrollY = currentY;

    const scroller = this.#scroller;
    const grid = this.#grid;
    if (!scroller || !grid || currentY <= previousY) return;

    const remaining = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
    if (remaining <= 1) return;

    const detailsStyle = getComputedStyle(this);
    if (detailsStyle.position !== 'sticky') return;

    const stickyTop = Number.parseFloat(detailsStyle.top) || 0;
    const visibleHeight = Math.max(window.innerHeight, this.getBoundingClientRect().height + stickyTop);
    const boundary = Math.max(0, grid.getBoundingClientRect().bottom + currentY - visibleHeight);

    // Only hand off while crossing the media end, not from a later page section.
    if (currentY <= boundary || previousY > boundary + 1) return;

    const requested = Math.min(currentY - Math.max(previousY, boundary), remaining);
    const before = scroller.scrollTop;
    scroller.scrollTop += requested;
    const consumed = scroller.scrollTop - before;
    if (consumed <= 0) return;

    // Preserve excess movement after the details reach their end. Updating the
    // baseline first also prevents our corrective scroll from handing off twice.
    this.#previousScrollY = currentY - consumed;
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  };
}

if (!customElements.get('product-details-component')) {
  customElements.define('product-details-component', ProductDetailsComponent);
}
